import { useMemo } from 'react';
import { useLoading, useSnackbar } from '@carcareers/contexts';
import { paymentApi } from '@carcareers/store';
import { PaymentCreateCustomerProfileDto, PaymentUpdateCustomerPaymentProfileDto } from '@libs/dto';
import { isError, getErrorsAsString } from '@carcareers/utils';

export const usePayment = () => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const [createCustomerProfile] = paymentApi.useCreateCustomerProfileMutation();
  const [createCustomerPaymentProfile] = paymentApi.useCreateCustomerPaymentProfileMutation();
  const [updateCustomerPaymentProfile] = paymentApi.useUpdateCustomerPaymentProfileMutation();
  const [deleteCustomerPaymentProfile] = paymentApi.useDeleteCustomerPaymentProfileMutation();
  const getCustomerProfile = paymentApi.useGetCustomerProfileQuery();

  return useMemo(
    () => ({
      getCustomerProfile,
      createProfile: async (customerProfile: PaymentCreateCustomerProfileDto) => {
        setLabel('Creating customer profile...');
        setLoading(true);
        const response = await createCustomerProfile(customerProfile);
        setLoading(false);
        return response;
      },
      createPayment: async (paymentProfile: PaymentCreateCustomerProfileDto) => {
        if (!paymentProfile) {
          toggleSnackbar('error', 'Missing payment information');
          return;
        }

        setLabel('Creating payment profile...');
        setLoading(true);
        const response = await createCustomerPaymentProfile(paymentProfile);

        if (isError(response)) {
          toggleSnackbar('error', getErrorsAsString(response));
        } else {
          toggleSnackbar('success', 'Payment profile created.');
        }

        setLoading(false);
        return response;
      },
      updatePayment: async (paymentProfile?: PaymentUpdateCustomerPaymentProfileDto) => {
        if (!paymentProfile) {
          toggleSnackbar('error', 'Missing payment information');
          return;
        }

        setLabel('Updating payment profile...');
        setLoading(true);
        const response = await updateCustomerPaymentProfile(paymentProfile);

        if (isError(response)) {
          toggleSnackbar('error', getErrorsAsString(response));
        } else {
          toggleSnackbar('success', 'Payment profile updated.');
        }

        setLoading(false);
        return response;
      },
      deletePayment: async (paymentProfileId?: string) => {
        if (!paymentProfileId) {
          toggleSnackbar('error', 'Missing payment profile ID');
          return;
        }

        setLabel('Deleting payment profile...');
        setLoading(true);
        const response = await deleteCustomerPaymentProfile(paymentProfileId);
        setLoading(false);
        return response;
      },
    }),
    [
      getCustomerProfile,
      setLabel,
      setLoading,
      toggleSnackbar,
      createCustomerProfile,
      updateCustomerPaymentProfile,
      deleteCustomerPaymentProfile,
      createCustomerPaymentProfile,
    ],
  );
};

